import React, { useState, useEffect } from 'react';
import { emailAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Search, Mail, Send, Check, X, Paperclip, FileText, Image, FileSpreadsheet, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export const EmailsPage = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [attachmentsDialogOpen, setAttachmentsDialogOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [availableDocuments, setAvailableDocuments] = useState([]);
  const [selectedAttachments, setSelectedAttachments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [formData, setFormData] = useState({
    recipient_email: '',
    subject: '',
    html_content: '',
    attachment_ids: [],
  });

  useEffect(() => {
    fetchEmails();
  }, []);

  const fetchEmails = async () => {
    try {
      const response = await emailAPI.getLogs();
      setEmails(response.data);
    } catch (error) {
      toast.error('Failed to fetch email logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableDocuments = async () => {
    setLoadingDocuments(true);
    try {
      const response = await emailAPI.getDocumentsForEmail();
      setAvailableDocuments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      setAvailableDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleOpenAttachments = () => {
    fetchAvailableDocuments();
    setAttachmentsDialogOpen(true);
  };

  const handleToggleAttachment = (docId) => {
    setSelectedAttachments(prev => {
      if (prev.includes(docId)) {
        return prev.filter(id => id !== docId);
      } else {
        return [...prev, docId];
      }
    });
  };

  const handleConfirmAttachments = () => {
    setFormData(prev => ({ ...prev, attachment_ids: selectedAttachments }));
    setAttachmentsDialogOpen(false);
  };

  const handleRemoveAttachment = (docId) => {
    setSelectedAttachments(prev => prev.filter(id => id !== docId));
    setFormData(prev => ({
      ...prev,
      attachment_ids: prev.attachment_ids.filter(id => id !== docId)
    }));
  };

  const getDocumentIcon = (type, mimeType) => {
    if (mimeType?.startsWith('image/')) return <Image className="h-4 w-4 text-blue-400" />;
    if (type === 'quote' || type === 'invoice') return <FileSpreadsheet className="h-4 w-4 text-green-400" />;
    return <FileText className="h-4 w-4 text-muted-foreground" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getAttachmentDetails = () => {
    return selectedAttachments.map(id => {
      const doc = availableDocuments.find(d => d.id === id);
      return doc || { id, filename: 'Unknown document' };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await emailAPI.send(formData);
      toast.success(`Email sent successfully${formData.attachment_ids.length > 0 ? ` with ${formData.attachment_ids.length} attachment(s)` : ''}`);
      fetchEmails();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const resetForm = () => {
    setDialogOpen(false);
    setSelectedAttachments([]);
    setFormData({
      recipient_email: '',
      subject: '',
      html_content: '',
      attachment_ids: [],
    });
  };

  const filteredEmails = emails.filter(email =>
    email.recipient_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    email.subject.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="emails-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Emails</h1>
          <p className="text-muted-foreground mt-1">Send and track emails</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setDialogOpen(true); }}>
          <DialogTrigger asChild>
            <Button className="uppercase font-bold tracking-wide rounded-sm" data-testid="compose-email-btn">
              <Plus className="h-4 w-4 mr-2" />
              Compose Email
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-lg">
            <DialogHeader>
              <DialogTitle>Compose Email</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>To *</Label>
                <Input
                  type="email"
                  value={formData.recipient_email}
                  onChange={(e) => setFormData({ ...formData, recipient_email: e.target.value })}
                  required
                  placeholder="recipient@example.com"
                  className="bg-input border-border rounded-sm"
                  data-testid="email-to"
                />
              </div>

              <div className="space-y-2">
                <Label>Subject *</Label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  required
                  placeholder="Email subject"
                  className="bg-input border-border rounded-sm"
                  data-testid="email-subject"
                />
              </div>

              <div className="space-y-2">
                <Label>Message *</Label>
                <Textarea
                  value={formData.html_content}
                  onChange={(e) => setFormData({ ...formData, html_content: e.target.value })}
                  required
                  className="bg-input border-border rounded-sm"
                  rows={6}
                  placeholder="Write your email message here..."
                  data-testid="email-content"
                />
                <p className="text-xs text-muted-foreground">HTML formatting is supported</p>
              </div>

              {/* Attachments Section */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <Paperclip className="h-4 w-4" />
                    Attachments
                  </Label>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    onClick={handleOpenAttachments}
                    className="rounded-sm"
                    data-testid="add-attachments-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Documents
                  </Button>
                </div>
                
                {formData.attachment_ids.length > 0 ? (
                  <div className="border border-border rounded-sm p-3 space-y-2 bg-muted/30">
                    {getAttachmentDetails().map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          {getDocumentIcon(doc.type, doc.mime_type)}
                          <span className="truncate max-w-[250px]">{doc.filename}</span>
                          {doc.size && <span className="text-xs text-muted-foreground">({formatFileSize(doc.size)})</span>}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveAttachment(doc.id)}
                          className="h-6 w-6"
                        >
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">No attachments added. Click "Add Documents" to attach photos, quotes, or POs.</p>
                )}
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={resetForm} className="rounded-sm">Cancel</Button>
                <Button type="submit" className="uppercase font-bold tracking-wide rounded-sm" disabled={sending} data-testid="send-email-btn">
                  <Send className="h-4 w-4 mr-2" />
                  {sending ? 'Sending...' : 'Send'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Attachments Selection Dialog */}
      <Dialog open={attachmentsDialogOpen} onOpenChange={setAttachmentsDialogOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="h-5 w-5 text-primary" />
              Select Documents to Attach
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            {loadingDocuments ? (
              <div className="flex items-center justify-center h-48">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : availableDocuments.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                <FileText className="h-12 w-12 mb-4 opacity-30" />
                <p>No documents available</p>
                <p className="text-sm">Upload documents to jobs first to attach them to emails</p>
              </div>
            ) : (
              <div className="space-y-1">
                {availableDocuments.map((doc) => (
                  <div 
                    key={doc.id} 
                    className={`flex items-center gap-3 p-3 rounded-sm cursor-pointer transition-colors ${
                      selectedAttachments.includes(doc.id) 
                        ? 'bg-primary/10 border border-primary/50' 
                        : 'hover:bg-muted/50 border border-transparent'
                    }`}
                    onClick={() => handleToggleAttachment(doc.id)}
                    data-testid={`doc-${doc.id}`}
                  >
                    <Checkbox 
                      checked={selectedAttachments.includes(doc.id)}
                      onCheckedChange={() => handleToggleAttachment(doc.id)}
                    />
                    {getDocumentIcon(doc.type, doc.mime_type)}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{doc.filename}</p>
                      <p className="text-xs text-muted-foreground">
                        {doc.type && <span className="uppercase">{doc.type}</span>}
                        {doc.size && <span> • {formatFileSize(doc.size)}</span>}
                        {doc.uploaded_at && <span> • {format(new Date(doc.uploaded_at), 'MMM d, yyyy')}</span>}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-between items-center pt-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              {selectedAttachments.length} document(s) selected
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setAttachmentsDialogOpen(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button onClick={handleConfirmAttachments} className="rounded-sm" data-testid="confirm-attachments-btn">
                <Paperclip className="h-4 w-4 mr-2" />
                Attach Selected
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search emails..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-input border-border rounded-sm"
          data-testid="email-search"
        />
      </div>

      {/* Email Logs Table */}
      <Card className="bg-card border-border rounded-sm">
        <CardHeader className="border-b border-border">
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            Email Activity Log
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredEmails.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <Mail className="h-12 w-12 mb-4" />
              <p>No email logs found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead>Status</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Related To</TableHead>
                  <TableHead>Attachments</TableHead>
                  <TableHead>Sent At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEmails.map((email) => (
                  <TableRow key={email.id} className="border-border" data-testid={`email-row-${email.id}`}>
                    <TableCell>
                      {email.status === 'sent' ? (
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30 rounded-sm">
                          <Check className="h-3 w-3 mr-1" />
                          Sent
                        </Badge>
                      ) : (
                        <Badge className="bg-red-500/20 text-red-400 border-red-500/30 rounded-sm">
                          <X className="h-3 w-3 mr-1" />
                          Failed
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{email.recipient_email}</TableCell>
                    <TableCell className="max-w-xs truncate">{email.subject}</TableCell>
                    <TableCell>
                      {email.related_type ? (
                        <Badge variant="outline" className="rounded-sm font-mono text-xs uppercase">
                          {email.related_type}
                        </Badge>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {email.attachments?.length > 0 ? (
                        <Badge variant="outline" className="rounded-sm">
                          <Paperclip className="h-3 w-3 mr-1" />
                          {email.attachments.length}
                        </Badge>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(email.sent_at), 'MMM d, yyyy HH:mm')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailsPage;
